/**
 * Admin Portal → Codes → Activity — `/admin/codes/activity` (Codes PR-8, on `GET /api/admin/codes/activity`):
 * one feed across the families — redemptions, codes handed out, referral rewards claimed, App Store
 * codes handed out, failed attempts, batches made and admin actions — newest first, paged with the
 * server's opaque `next_cursor` (events sharing a time are never skipped).
 *
 * Every admin role reads; support and viewer see codes masked. Never an email, an IP, or an App Store code.
 *
 * Web-only (the admin SPA).
 */

import React, { useCallback, useEffect, useState } from 'react';
import { AdminButton, AdminCard } from '../../../components/admin/ui';
import { fmtDateTime, hint, td, th } from '../partners/PartnersShared';
import { CodesAdmin } from './codesAdminService';
import {
  ACTIVITY_KINDS, ACTIVITY_KIND_LABELS, describeEvent, familyLabel, type ActivityEvent, type ActivityKind,
} from './codesAreaLogic';
import { CodesHeader, ErrorLine, PAGE, readModelError } from './CodesArea';

export function CodesActivity() {
  const [kinds, setKinds] = useState<string[]>([...ACTIVITY_KINDS]);
  const [events, setEvents] = useState<ActivityEvent[]>([]);
  const [next, setNext] = useState<string | null>(null);
  const [masked, setMasked] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (cursor: string | null) => {
    setBusy(true);
    setError(null);
    try {
      const r = await CodesAdmin.activity(kinds, cursor);
      setEvents((prev) => (cursor ? [...prev, ...(r.events || [])] : (r.events || [])));
      setNext(r.next_cursor ?? null);
      setMasked(!!r.codes_masked);
    } catch (e) {
      setError(readModelError(e));
    } finally {
      setBusy(false);
    }
  }, [kinds]);

  useEffect(() => { void load(null); }, [load]);

  const toggle = (k: string) =>
    setKinds((ks) => (ks.includes(k) ? ks.filter((x) => x !== k) : [...ks, k]));

  return (
    <div style={PAGE}>
      <CodesHeader title="Activity" subtitle="Everything that happened to a code, newest first." />
      <AdminCard style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 13 }} data-testid="activity-kinds">
          {ACTIVITY_KINDS.map((k) => (
            <label key={k} style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              <input type="checkbox" checked={kinds.includes(k)} onChange={() => toggle(k)} />
              {ACTIVITY_KIND_LABELS[k as ActivityKind]}
            </label>
          ))}
        </div>
        {masked && <p style={hint}>Codes are masked for your admin role.</p>}
      </AdminCard>
      <ErrorLine text={error} />
      {kinds.length === 0 ? <p style={hint}>Choose at least one kind of event.</p> : (
        <>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr><th style={th}>When</th><th style={th}>What</th><th style={th}>Family</th><th style={th}>Event</th></tr></thead>
            <tbody>
              {events.length === 0 && !busy && <tr><td style={td} colSpan={4}>No events.</td></tr>}
              {events.map((e, i) => (
                <tr key={`${e.kind}:${e.at}:${i}`}>
                  <td style={td}>{fmtDateTime(e.at)}</td>
                  <td style={td}>{ACTIVITY_KIND_LABELS[e.kind as ActivityKind] ?? e.kind}</td>
                  <td style={td}>{e.family ? familyLabel(e.family) : '—'}</td>
                  <td style={td}>{describeEvent(e)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {next && (
            <AdminButton size="sm" variant="secondary" disabled={busy} style={{ marginTop: 8 }} onClick={() => void load(next)}>
              Older events
            </AdminButton>
          )}
        </>
      )}
    </div>
  );
}

export default CodesActivity;
